/**
 * Tag Popup
 *
 * Floating panel that appears when pressing 't' on a node.
 * Shows tag checkboxes and allows creating new tags.
 *
 * For a multi-selection (openMany) each tag shows whether all, some or none
 * of the nodes carry it; a click sets it on all of them (or clears it when
 * all have it), and the popup stays open for more changes.
 */
import { t } from '../../../shared/i18n/index.js';
import { tagLabel } from '../../../engine/core/tag-filter.js';

let popupEl = null;
let callbacks = null;
let currentNodeId = null;
let currentNodeIds = null; // multi-selection, or null
let skippedNotice = 0; // nodes the last multi-selection change could not reach

/**
 * Initialize the tag popup.
 * @param {Object} cbs
 * @param {Function} cbs.getTagDefinitions - () => [{id, name}]
 * @param {Function} cbs.addTagDefinition - ({id, name}) => void
 * @param {Function} cbs.toggleNodeTag - (nodeId, tagId) => boolean
 * @param {Function} cbs.setNodesTag - (nodeIds, tagId, on) => {changed, skipped}
 * @param {Function} cbs.nodeHasTag - (node, tagId) => boolean
 * @param {Function} cbs.generateId - () => string
 * @param {Function} cbs.find - (nodeId) => node
 */
export function init(cbs) {
  callbacks = cbs;
}

/**
 * Open the tag popup for a node.
 * @param {string} nodeId
 * @param {number} [mouseX]
 * @param {number} [mouseY]
 */
export function open(nodeId, mouseX, mouseY) {
  openFor([nodeId], mouseX, mouseY);
}

/**
 * Open the tag popup for several nodes (multi-selection).
 * @param {string[]} nodeIds
 * @param {number} [mouseX]
 * @param {number} [mouseY]
 */
export function openMany(nodeIds, mouseX, mouseY) {
  openFor(nodeIds, mouseX, mouseY);
}

/** Whether the popup is showing. */
export function isOpen() {
  return !!popupEl;
}

function openFor(nodeIds, mouseX, mouseY) {
  close();
  if (!callbacks || !nodeIds?.length) return;

  const nodeId = nodeIds[0];
  currentNodeId = nodeId;
  currentNodeIds = nodeIds.length > 1 ? nodeIds.slice() : null;
  skippedNotice = 0;
  const node = callbacks.find(nodeId);
  if (!node) return;

  popupEl = document.createElement('div');
  popupEl.className = 'tag-popup';

  renderContent();

  popupEl.style.position = 'fixed';
  if (mouseX != null && mouseY != null) {
    popupEl.style.left = Math.min(mouseX, window.innerWidth - 220) + 'px';
    popupEl.style.top = Math.min(mouseY, window.innerHeight - 300) + 'px';
  } else {
    const nodeEl = document.querySelector(`[data-id="${nodeId}"]`);
    if (nodeEl) {
      const rect = nodeEl.getBoundingClientRect();
      popupEl.style.left = Math.min(rect.right + 8, window.innerWidth - 220) + 'px';
      popupEl.style.top = Math.max(rect.top, 8) + 'px';
    } else {
      popupEl.style.left = '50%';
      popupEl.style.top = '50%';
      popupEl.style.transform = 'translate(-50%, -50%)';
    }
  }

  document.body.appendChild(popupEl);

  setTimeout(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleKeydown);
  }, 0);
}

export function close() {
  if (popupEl) {
    popupEl.remove();
    popupEl = null;
    currentNodeId = null;
    currentNodeIds = null;
    document.removeEventListener('mousedown', handleOutsideClick);
    document.removeEventListener('keydown', handleKeydown);
  }
}

function handleOutsideClick(e) {
  if (popupEl && !popupEl.contains(e.target)) {
    close();
  }
}

function handleKeydown(e) {
  if (e.key === 'Escape') {
    e.preventDefault();
    e.stopPropagation();
    close();
  }
}

function renderContent() {
  if (!popupEl || !callbacks) return;

  // Pass the node so we pull tag definitions from the node's host doc
  // (linked docs have their own tag lists; cross-doc ids don't resolve).
  const defs = callbacks.getTagDefinitions(currentNodeId);
  const node = callbacks.find(currentNodeId);

  popupEl.innerHTML = '';

  // Header
  const header = document.createElement('div');
  header.className = 'tag-popup-header';
  const title = document.createElement('span');
  title.className = 'tag-popup-title';
  title.textContent = currentNodeIds ? t('app.tagPopup.titleMany', { count: currentNodeIds.length }) : t('common.tags');
  header.appendChild(title);
  const closeBtn = document.createElement('button');
  closeBtn.className = 'tag-popup-close';
  closeBtn.innerHTML = '\u00D7';
  closeBtn.title = t('common.close');
  closeBtn.addEventListener('click', (e) => { e.stopPropagation(); close(); });
  header.appendChild(closeBtn);
  popupEl.appendChild(header);

  if (currentNodeIds) {
    renderManyPills(defs);
  } else if (defs.length > 0) {
    const list = document.createElement('div');
    list.className = 'tag-popup-list';

    const mandatoryIds = new Set(defs.filter(d => d.mandatory).map(d => d.id));
    const allPills = [];

    for (const tag of defs) {
      const has = callbacks.nodeHasTag(node, tag.id);
      const pill = document.createElement('button');
      pill.type = 'button';
      pill.className = 'tag-popup-pill' + (has ? ' active' : '');
      pill.textContent = tagLabel(tag);
      pill.dataset.tagId = tag.id;
      pill.addEventListener('click', () => {
        const wasActive = pill.classList.contains('active');
        // Mandatory tags: deactivate siblings before activating
        if (!wasActive && mandatoryIds.has(tag.id)) {
          allPills.forEach(p => {
            if (mandatoryIds.has(p.dataset.tagId)) p.classList.remove('active');
          });
        }
        callbacks.toggleNodeTag(currentNodeId, tag.id);
        pill.classList.toggle('active');
        setTimeout(close, 300);
      });
      allPills.push(pill);
      list.appendChild(pill);
    }

    popupEl.appendChild(list);
  }

  // Add new tag input
  const addRow = document.createElement('div');
  addRow.className = 'tag-popup-add';
  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'tag-popup-add-input';
  input.placeholder = t('app.tagPopup.newTag');
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const name = input.value.trim();
      if (!name) return;
      const tagId = callbacks.generateId();
      callbacks.addTagDefinition({ id: tagId, name });
      if (currentNodeIds) skippedNotice = callbacks.setNodesTag(currentNodeIds, tagId, true)?.skipped || 0;
      else callbacks.toggleNodeTag(currentNodeId, tagId);
      renderContent();
    }
    if (e.key === 'Escape') { e.stopPropagation(); close(); return; }
    e.stopPropagation();
  });
  addRow.appendChild(input);
  popupEl.appendChild(addRow);
}

/**
 * Pills for a multi-selection: all (active), some (partial) or none of the
 * nodes carry the tag. A click sets it on all of them, or clears it when
 * all have it; Todo / In Progress / Done stay exclusive.
 */
function renderManyPills(defs) {
  if (!defs.length) return;
  const list = document.createElement('div');
  list.className = 'tag-popup-list';
  const nodes = currentNodeIds.map((id) => callbacks.find(id)).filter(Boolean);
  const mandatoryIds = new Set(defs.filter((d) => d.mandatory).map((d) => d.id));
  const pills = [];
  const show = (pill, state) => {
    pill.classList.toggle('active', state === 'all');
    pill.classList.toggle('partial', state === 'some');
    pill.setAttribute('aria-pressed', state === 'all' ? 'true' : state === 'some' ? 'mixed' : 'false');
    pill.dataset.state = state;
    pill.title = state === 'all' ? t('app.tagPopup.stateAll')
      : state === 'some' ? t('app.tagPopup.stateSome')
      : t('app.tagPopup.stateNone');
  };
  for (const tag of defs) {
    const count = nodes.filter((n) => callbacks.nodeHasTag(n, tag.id)).length;
    const pill = document.createElement('button');
    pill.type = 'button';
    pill.className = 'tag-popup-pill';
    pill.textContent = tagLabel(tag);
    pill.dataset.tagId = tag.id;
    show(pill, count === nodes.length ? 'all' : count ? 'some' : 'none');
    pill.addEventListener('click', () => {
      const on = pill.dataset.state !== 'all';
      const result = callbacks.setNodesTag(currentNodeIds, tag.id, on);
      skippedNotice = result?.skipped || 0;
      if (on && mandatoryIds.has(tag.id)) {
        pills.forEach((p) => { if (p !== pill && mandatoryIds.has(p.dataset.tagId)) show(p, 'none'); });
      }
      show(pill, on ? 'all' : 'none');
      // Then show what the nodes really carry (skipped nodes kept theirs).
      setTimeout(() => { if (popupEl && currentNodeIds) renderContent(); }, 0);
    });
    pills.push(pill);
    list.appendChild(pill);
  }
  popupEl.appendChild(list);
  if (skippedNotice) {
    const note = document.createElement('div');
    note.className = 'tag-popup-note';
    note.textContent = t('app.tagPopup.skipped', { count: skippedNotice });
    popupEl.appendChild(note);
  }
}
